import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import type { FollowUpPriority, VisitOrderRef, VisitRow } from '@fitting-room/contracts'
import { VISIT_STATUS_TRANSITIONS, asksWhyNoPurchase, hasLiveOrder } from '@fitting-room/contracts/appointment-status'
import { CalendarClock, Link2, Pencil, Receipt } from 'lucide-react'
import { assetUrl } from '../../lib/api'
import {
  useAppointment,
  useAppointmentFavorites,
  useAppointmentTypes,
  useAssignAppointment,
  useAssignees,
  useBackOffice,
  useChargeNoShowFee,
  useCloseFollowUp,
  useMintAppointmentLink,
  useOrder,
  useRecordAppointmentOutcome,
  useSetAppointmentStatus,
  useSetFollowUp,
  useUpdateAppointment,
  useVisitCard,
} from '../../lib/adminQueries'
import {
  FOLLOW_UP_PRIORITY_LABEL,
  assigneeName,
  formatMinutes,
  formatUsd,
  labelForKey,
  offersFollowUp,
  optionsWithStored,
  statusActionLabel,
} from '../../lib/appointments'
import { BOUTIQUE_LOCALE, boutiqueDateTime, boutiqueDayOf, boutiqueTime, boutiqueToday } from '../../lib/boutique'
import { formatDay } from '../../lib/format'
import { addedByLabel, gownDetail } from '../../lib/favorites'
import { shiftDay } from '../../lib/months'
import { useStaffToken } from '../../lib/staffToken'
import { AssigneeSelect } from './AssigneeSelect'
import { DatePicker } from './DatePicker'
import { Drawer } from './Drawer'
import { EditDetailsDialog } from './EditDetailsDialog'
import { FavoritesPicker } from './FavoritesPicker'
import { GownThumb } from './GownThumb'
import { LinkToSend } from './NewAppointmentDialog'
import { RescheduleDialog } from './RescheduleDialog'
import { StaffMark } from './StaffMark'
import { SessionPill, VisitPill } from './StatusPill'
import { LinkToOrderDialog } from './orders/OrderAppointments'
import { OrderStatusBadge } from './orders/OrderStatusBadge'
import { OrderAmountLabel, VisitOrdersTotal } from './orders/VisitOrderAmounts'

const PRIORITIES: readonly FollowUpPriority[] = ['urgent', 'high', 'normal', 'low']

/**
 * One appointment, everything a stylist does to it in one place. Ported from
 * tperent's `AppointmentDetailDrawer` — its action buttons, its status
 * buttons at the foot — restyled to the console and without what Koalendar or
 * LINE own here.
 *
 * Its sections run in the order the owner reads an appointment: who the
 * customer is, who has them, what they chose online, whether they bought —
 * which is whether there is an order on it, and nothing a stylist sets — and
 * what happens next. What they chose is the drawer's own list, titled by
 * style #; the pull sheet still exists at its address but is no longer a
 * place the console sends anyone.
 *
 * The row it was opened from is drawn at once, and the detail — which adds
 * the outcome — replaces it when it arrives, so opening a row never flashes
 * an empty drawer.
 */
export function AppointmentDrawer({
  visitId,
  row,
  onClose,
  onPrevious,
  onNext,
}: {
  readonly visitId: string | null
  readonly row?: VisitRow | undefined
  readonly onClose: () => void
  readonly onPrevious?: (() => void) | undefined
  readonly onNext?: (() => void) | undefined
}) {
  const { token } = useStaffToken()
  const detail = useAppointment(token, visitId)
  const visit = detail.data?.visit ?? row
  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState(false)

  return (
    <>
      <Drawer
        open={visitId !== null}
        onOpenChange={(next) => (next ? undefined : onClose())}
        eyebrow={visit ? formatDay(boutiqueDayOf(visit.scheduledAt), BOUTIQUE_LOCALE) : undefined}
        title={visit?.customerName ?? 'Appointment'}
        onPrevious={onPrevious}
        onNext={onNext}
      >
        {detail.isError && !visit ? <p className="st-alert">Couldn&rsquo;t load this appointment. It may have been removed.</p> : null}
        {visit ? (
          <AppointmentBody
            key={visit.id}
            visit={visit}
            onEdit={() => setEditing(true)}
            onReschedule={() => setMoving(true)}
          />
        ) : (
          <p className="st-meta">Loading…</p>
        )}
      </Drawer>
      <EditDetailsDialog visit={editing && visit ? visit : null} onClose={() => setEditing(false)} />
      <RescheduleDialog visit={moving && visit ? visit : null} onClose={() => setMoving(false)} />
    </>
  )
}

function AppointmentBody({
  visit,
  onEdit,
  onReschedule,
}: {
  readonly visit: VisitRow
  readonly onEdit: () => void
  readonly onReschedule: () => void
}) {
  const { token } = useStaffToken()
  const types = useAppointmentTypes(token).data?.types
  const settings = useBackOffice(token).data
  const members = useAssignees(token).data?.members ?? []
  const type = types?.find((t) => t.key === visit.tierKey)
  // Collecting a gown already bought: no fitting room, no picks, nothing to sell.
  // Which it is waits for the types: drawn as a fitting until then, a pickup
  // would offer a fitting room link and a new order for a moment.
  const known = types !== undefined
  const pickup = type?.kind === 'pickup'
  const minutes = visit.durationMinutes ?? type?.durationMinutes
  const end = minutes ? new Date(new Date(visit.scheduledAt).getTime() + minutes * 60_000).toISOString() : undefined
  const color = settings?.tierColors[visit.tierKey]
  const mark = members.find((m) => m.id === visit.assigneeId)?.calendarMark
  // A minted link is shown until the drawer moves on; it is never fetched again.
  const [link, setLink] = useState<string | null>(null)

  return (
    <div>
      <section className="st-card mb-3 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="st-display text-2xl tabular-nums">
              {boutiqueTime(visit.scheduledAt)}
              {end ? <span className="st-meta text-base"> – {boutiqueTime(end)}</span> : null}
            </div>
            <div className="st-meta mt-1 flex items-center gap-1.5">
              <span className="st-tier-dot" style={color ? { background: color } : undefined} aria-hidden />
              {type?.label ?? visit.tierKey}
              {minutes ? <span>· {formatMinutes(minutes)}</span> : null}
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-1.5">
            <VisitPill status={visit.status} />
            {known && !pickup ? <SessionPill status={visit.sessionStatus} /> : null}
          </div>
        </div>
        <UndoStatus visit={visit} />
      </section>

      <div className={`mb-4 grid gap-2 ${pickup ? 'grid-cols-2' : 'grid-cols-3'}`}>
        <button type="button" className="st-btn-ghost" onClick={onReschedule} disabled={visit.status === 'cancelled'}>
          <CalendarClock size={14} aria-hidden />
          Reschedule
        </button>
        <button type="button" className="st-btn-ghost" onClick={onEdit}>
          <Pencil size={14} aria-hidden />
          Edit details
        </button>
        {known && !pickup ? <FittingRoomLinkButton visitId={visit.id} hasSession={visit.sessionStatus !== 'none'} onMinted={setLink} /> : null}
      </div>
      {link ? (
        <div className="mb-4">
          <LinkToSend url={link} />
        </div>
      ) : null}

      <Section title="Details">
        <Field label="Contact">
          {visit.customerEmail || visit.customerPhone ? (
            <ul className="space-y-0.5">
              {visit.customerPhone ? <li className="tabular-nums">{visit.customerPhone}</li> : null}
              {visit.customerEmail ? <li>{visit.customerEmail}</li> : null}
            </ul>
          ) : (
            // Not an error: a booking pushed from elsewhere may carry no contact at all.
            <span className="st-meta">Nothing on file</span>
          )}
        </Field>
        {visit.guestCount === undefined ? null : <Field label="Guests">{visit.guestCount}</Field>}
        <Field label="Source">{labelForKey(settings?.bookingSources, visit.source) ?? <span className="st-meta">Not recorded</span>}</Field>
        {visit.weddingDate ? <Field label="Wedding date">{formatDay(visit.weddingDate, BOUTIQUE_LOCALE)}</Field> : null}
        {visit.budgetMinor !== undefined ? <Field label="Budget">{formatUsd(visit.budgetMinor)}</Field> : null}
      </Section>

      <Section title="Bridal stylist">
        <AssignControl visit={visit} mark={mark} />
      </Section>

      <CardOnFileSection visit={visit} />

      {known && !pickup ? <FavoritesSection visit={visit} /> : null}

      {known ? (
        <Section title="Order">
          <OrderControl visit={visit} pickup={pickup} />
        </Section>
      ) : null}

      {/* Asked only when they came and left with no order; see
          `asksWhyNoPurchase`. Writing an order clears the reason. */}
      {known && asksWhyNoPurchase(visit, pickup) ? (
        <Section title="Why they didn’t buy">
          <NoPurchaseReasonControl visit={visit} />
        </Section>
      ) : null}

      {/* The same question, or a follow-up already set; see `offersFollowUp`. */}
      {known && offersFollowUp(visit, pickup) ? (
        <Section title="Follow-up">
          <FollowUpControl visit={visit} />
        </Section>
      ) : null}

      <Section title="Notes">
        <NotesControl visit={visit} />
      </Section>

      <StatusActions visit={visit} />
    </div>
  )
}

/**
 * The card saved when the customer booked online (`docs/23`), and the
 * no-show fee: charged only when somebody here presses it, only on a no-show
 * or a late cancel, and once. Nothing shows for an appointment booked any
 * other way.
 */
function CardOnFileSection({ visit }: { readonly visit: VisitRow }) {
  const { token, role } = useStaffToken()
  const [askOwner, setAskOwner] = useState(false)
  const query = useVisitCard(token, visit.id)
  const charge = useChargeNoShowFee(token, visit.id)
  const card = query.data?.card
  if (!card) return null
  const charged = card.charge
  const due = visit.status === 'no_show' || visit.status === 'cancelled'
  return (
    <Section title="Card on file">
      <p className="text-sm" data-testid="card-on-file">
        {card.brand ? `${card.brand.charAt(0).toUpperCase()}${card.brand.slice(1)}` : 'Card'}
        {card.last4 ? ` ending in ${card.last4}` : ''}
        <span className="st-meta"> · saved {boutiqueDateTime(card.savedAt)}</span>
      </p>
      <p className="st-meta mb-2 text-xs">{card.policy}</p>
      {charged ? (
        <p className={charged.status === 'failed' || charged.status === 'requires_action' ? 'st-alert text-sm' : 'st-ok text-sm'} role="status">
          {charged.status === 'succeeded'
            ? `No-show fee of ${formatUsd(charged.amountMinor)} charged ${boutiqueDateTime(charged.at)}.`
            : charged.status === 'processing'
              ? `No-show fee of ${formatUsd(charged.amountMinor)} is processing.`
              : charged.status === 'requires_action'
                ? 'The bank wants the customer to approve the charge, so it didn’t go through. Ask them to pay another way.'
                : `The charge was declined${charged.error ? `: ${charged.error}` : '.'}`}
        </p>
      ) : null}
      {due && (!charged || charged.status === 'failed') && card.feeMinor > 0 ? (
        <button
          type="button"
          className="st-btn-ghost mt-1"
          disabled={charge.isPending || !query.data?.stripeConfigured}
          onClick={() => {
            // A stylist sees the button and what it would charge; the charge itself is an owner's.
            if (role !== 'owner') return setAskOwner(true)
            if (window.confirm(`Charge the ${formatUsd(card.feeMinor)} no-show fee to this card?`)) charge.mutate()
          }}
        >
          {charge.isPending ? 'Charging…' : charged ? 'Try the charge again' : `Charge no-show fee · ${formatUsd(card.feeMinor)}`}
        </button>
      ) : null}
      {askOwner ? (
        <p className="st-alert mt-1 text-sm" role="alert">
          Only an owner can charge the no-show fee. Ask an owner to charge it.
        </p>
      ) : null}
      {charge.isError ? <p className="st-alert mt-1 text-sm">{charge.error.message}</p> : null}
    </Section>
  )
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="mb-4">
      <h3 className="st-eyebrow mb-1.5">{title}</h3>
      {children}
    </section>
  )
}

function Field({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex gap-3 border-b py-2 text-sm" style={{ borderColor: 'var(--st-rule)' }}>
      <span className="st-meta w-28 shrink-0 text-xs">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}

/**
 * The way back from a status, at the top where the status is shown. tperent
 * put "undo completed" in the drawer's header for the same reason: the
 * buttons at the foot are where an appointment goes NEXT, and correcting a
 * mis-click is a different act.
 */
function UndoStatus({ visit }: { readonly visit: VisitRow }) {
  const { token } = useStaffToken()
  const status = useSetAppointmentStatus(token, visit.id)
  if (visit.status === 'booked' || !VISIT_STATUS_TRANSITIONS[visit.status].includes('booked')) return null
  return (
    <div className="mt-2 flex items-center gap-2 text-xs">
      <button
        type="button"
        className="underline"
        style={{ color: 'var(--st-gold-deep)' }}
        onClick={() => status.mutate({ status: 'booked' })}
        disabled={status.isPending}
      >
        {statusActionLabel(visit.status, 'booked')}
      </button>
      {status.isError ? <span className="text-[color:#8d3f2c]">{status.error.message}</span> : null}
    </div>
  )
}

function StatusActions({ visit }: { readonly visit: VisitRow }) {
  const { token } = useStaffToken()
  const status = useSetAppointmentStatus(token, visit.id)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [tooEarly, setTooEarly] = useState(false)
  const forward = VISIT_STATUS_TRANSITIONS[visit.status].filter((to) => to !== 'booked')

  if (forward.length === 0) {
    return (
      <p className="st-meta mt-2 text-center text-xs">
        This appointment is {visit.status === 'cancelled' ? 'canceled' : visit.status === 'no_show' ? 'marked a no-show' : 'completed'}. Pressed
        it by mistake? Undo it at the top.
      </p>
    )
  }

  return (
    <div className="mt-4 flex flex-col gap-2">
      {tooEarly ? (
        <p className="st-alert" role="alert">
          This appointment hasn&rsquo;t started yet. Mark it a no-show once its time has passed.
        </p>
      ) : status.isError ? (
        <p className="st-alert" role="alert">
          {status.error.message}
        </p>
      ) : null}
      {forward.map((to) =>
        to === 'cancelled' ? (
          confirmCancel ? (
            <div key={to} className="st-notice flex flex-wrap items-center gap-2">
              <span className="flex-1">Cancel it? Nothing is sent to the customer.</span>
              <button type="button" className="st-btn-ghost" onClick={() => setConfirmCancel(false)}>
                Keep it
              </button>
              <button
                type="button"
                className="st-btn"
                disabled={status.isPending}
                onClick={() => status.mutate({ status: 'cancelled' }, { onSuccess: () => setConfirmCancel(false) })}
              >
                Cancel appointment
              </button>
            </div>
          ) : (
            <button key={to} type="button" className="st-btn-ghost w-full" onClick={() => setConfirmCancel(true)} disabled={status.isPending}>
              {statusActionLabel(visit.status, to)}
            </button>
          )
        ) : (
          <button
            key={to}
            type="button"
            className={to === 'completed' ? 'st-btn w-full' : 'st-btn-ghost w-full'}
            onClick={() => {
              // Nobody has failed to show up to an appointment that hasn't started: say so, and send nothing.
              if (to === 'no_show' && Date.now() < Date.parse(visit.scheduledAt)) return setTooEarly(true)
              setTooEarly(false)
              status.mutate({ status: to })
            }}
            disabled={status.isPending}
            data-action-status={to}
          >
            {statusActionLabel(visit.status, to)}
          </button>
        ),
      )}
    </div>
  )
}

/** The stylist the appointment is put against — whose customer it is. Their calendar mark beside the name. */
function AssignControl({ visit, mark }: { readonly visit: VisitRow; readonly mark: string | undefined }) {
  const { token } = useStaffToken()
  const assignees = useAssignees(token)
  const assign = useAssignAppointment(token, visit.id)
  if (assignees.isError) return <p className="st-meta text-sm">Couldn&rsquo;t load the team.</p>
  const members = assignees.data?.members ?? []
  return (
    <div>
      <div className="flex items-center gap-2">
        {mark ? <StaffMark mark={mark} name={assigneeName(members, visit.assigneeId)} /> : null}
        <AssigneeSelect
          members={members}
          value={visit.assigneeId ?? null}
          disabled={assign.isPending}
          onChange={(assigneeId) => assign.mutate({ assigneeId })}
        />
      </div>
      {assign.isError ? <p className="mt-1 text-xs text-[color:#8d3f2c]">{assign.error.message}</p> : null}
    </div>
  )
}

/**
 * The customer's list, so the stylist sees it where they read the
 * appointment: what the customer chose in their online fitting room, and
 * what a stylist added, said beneath it. Titled by style # — the number the
 * gown is found by on the rail and written on the order — with the name the
 * customer saw beneath it; a gown with no style # falls back to its name.
 *
 * Edit opens the picker (`FavoritesPicker`); its Save answers with the list
 * as it now stands, which redraws this section in place.
 */
function FavoritesSection({ visit }: { readonly visit: VisitRow }) {
  const { token } = useStaffToken()
  const favorites = useAppointmentFavorites(token, visit.id)
  const [editing, setEditing] = useState(false)
  const data = favorites.data
  // No published flow means no fitting room to open, and nowhere to add to.
  const canEdit = data?.slot !== undefined

  return (
    <section className="mb-4">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="st-eyebrow">Her favorites</h3>
        <button
          type="button"
          className="st-btn-quiet st-btn-sm"
          aria-label="Edit favorites"
          disabled={!canEdit}
          title={data && !canEdit ? 'There’s no published fitting room to add gowns to.' : undefined}
          onClick={() => setEditing(true)}
        >
          <Pencil size={12} aria-hidden />
          Edit
        </button>
      </div>
      {favorites.isError ? (
        <p className="st-meta text-sm">Couldn&rsquo;t load their favorites.</p>
      ) : !data ? (
        <p className="st-meta text-sm">Loading…</p>
      ) : data.favorites.length === 0 ? (
        <p className="st-meta text-sm">
          {visit.sessionStatus === 'none'
            ? 'This appointment has no fitting room. Add gowns with Edit and one is opened for it — nothing is sent to the customer.'
            : 'Nothing picked in their fitting room yet.'}
        </p>
      ) : (
        <ul className="space-y-1.5" aria-label="Her favorites">
          {data.favorites.map((favorite) => (
            <li key={favorite.garmentId} className="flex items-center gap-3 text-sm" data-garment-id={favorite.garmentId} data-added-by={favorite.addedBy}>
              <GownThumb src={favorite.imageUrl ? assetUrl(favorite.imageUrl) : undefined} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium tabular-nums">{favorite.code ?? favorite.name}</span>
                <span className="st-meta block truncate text-xs">{gownDetail(favorite, favorite.slotLabel)}</span>
                {favorite.addedBy === 'staff' ? (
                  <span className="block truncate text-xs" style={{ color: 'var(--st-gold-deep)' }}>
                    {addedByLabel(favorite)}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {data ? (
        <FavoritesPicker open={editing} onClose={() => setEditing(false)} visitId={visit.id} customerName={visit.customerName} favorites={data} />
      ) : null}
    </section>
  )
}


/**
 * Why a customer who came left without an order — one of the owner's
 * reasons. Shown only then (`asksWhyNoPurchase`); there is no "Sold" to set
 * beside it, because the order is the answer, and writing one clears this.
 * Saved through the pull sheet's outcome endpoint, which leaves the stored
 * outcome alone when it is sent only a reason.
 */
function NoPurchaseReasonControl({ visit }: { readonly visit: VisitRow }) {
  const { token } = useStaffToken()
  const reasons = useBackOffice(token).data?.noPurchaseReasons
  const record = useRecordAppointmentOutcome(token, visit.id)

  return (
    <div>
      {/* The section is titled already, so the select carries its name for a screen reader only. */}
      <select
        className="st-select w-full"
        aria-label="Why they didn’t buy"
        value={visit.noPurchaseReason ?? ''}
        disabled={record.isPending}
        onChange={(event) => record.mutate({ pickOutcomes: {}, noPurchaseReason: event.target.value || null })}
      >
        <option value="">Not recorded</option>
        {optionsWithStored(reasons, visit.noPurchaseReason).map((entry) => (
          <option key={entry.key} value={entry.key}>
            {entry.label}
          </option>
        ))}
      </select>
      {record.isError ? <p className="mt-1 text-xs text-[color:#8d3f2c]">{record.error.message}</p> : null}
    </div>
  )
}

/**
 * When to get back to the customer. Snoozing is moving the date — the
 * follow-up queue orders by it — so the two quick buttons are "tomorrow" and
 * "a week from today", and the date picker is any other day.
 */
function FollowUpControl({ visit }: { readonly visit: VisitRow }) {
  const { token } = useStaffToken()
  const set = useSetFollowUp(token, visit.id)
  const close = useCloseFollowUp(token, visit.id)
  const [editing, setEditing] = useState(false)
  const today = boutiqueToday()
  const error = set.error ?? close.error

  if (!visit.followUpOn || editing) {
    return (
      <FollowUpForm
        visit={visit}
        pending={set.isPending}
        onCancel={visit.followUpOn ? () => setEditing(false) : undefined}
        onSave={(body) => set.mutate(body, { onSuccess: () => setEditing(false) })}
        error={set.error?.message}
      />
    )
  }

  const done = visit.followUpDoneAt !== undefined
  const overdue = !done && visit.followUpOn < today
  const snooze = (on: string) =>
    set.mutate({ on, priority: visit.followUpPriority ?? 'normal', ...(visit.followUpNote ? { note: visit.followUpNote } : {}) })

  return (
    <div className="st-card px-3 py-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={overdue ? 'font-medium text-[color:#8d3f2c]' : 'font-medium'}>{formatDay(visit.followUpOn, BOUTIQUE_LOCALE)}</span>
        {overdue ? <span className="st-badge st-badge--alert">Overdue</span> : null}
        {done ? <span className="st-badge st-badge--submitted">Done</span> : null}
        <span className="st-badge st-priority" data-priority={visit.followUpPriority ?? 'normal'}>
          {FOLLOW_UP_PRIORITY_LABEL[visit.followUpPriority ?? 'normal']}
        </span>
      </div>
      {visit.followUpNote ? <p className="mt-1.5 whitespace-pre-wrap">{visit.followUpNote}</p> : null}
      {done ? <p className="st-meta mt-1 text-xs">Marked done {boutiqueDateTime(visit.followUpDoneAt as string)}.</p> : null}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {done ? null : (
          <>
            <button type="button" className="st-btn" onClick={() => close.mutate('done')} disabled={close.isPending}>
              Done
            </button>
            <button type="button" className="st-btn-ghost" onClick={() => snooze(shiftDay(today, 1))} disabled={set.isPending}>
              Tomorrow
            </button>
            <button type="button" className="st-btn-ghost" onClick={() => snooze(shiftDay(today, 7))} disabled={set.isPending}>
              In a week
            </button>
          </>
        )}
        <button type="button" className="st-btn-quiet" onClick={() => setEditing(true)}>
          {done ? 'Reopen' : 'Change'}
        </button>
        <button type="button" className="st-btn-quiet" onClick={() => close.mutate('clear')} disabled={close.isPending}>
          Remove
        </button>
      </div>
      {error ? <p className="mt-1 text-xs text-[color:#8d3f2c]">{error.message}</p> : null}
    </div>
  )
}

function FollowUpForm({
  visit,
  pending,
  onSave,
  onCancel,
  error,
}: {
  readonly visit: VisitRow
  readonly pending: boolean
  readonly onSave: (body: { on: string; priority: FollowUpPriority; note: string | null }) => void
  readonly onCancel: (() => void) | undefined
  readonly error: string | undefined
}) {
  const [on, setOn] = useState(visit.followUpOn ?? shiftDay(boutiqueToday(), 2))
  const [priority, setPriority] = useState<FollowUpPriority>(visit.followUpPriority ?? 'normal')
  const [note, setNote] = useState(visit.followUpNote ?? '')
  return (
    <div className="st-card px-3 py-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <DatePicker value={on} onChange={setOn} label="Follow up on" />
        <select className="st-select" aria-label="Priority" value={priority} onChange={(event) => setPriority(event.target.value as FollowUpPriority)}>
          {PRIORITIES.map((value) => (
            <option key={value} value={value}>
              {FOLLOW_UP_PRIORITY_LABEL[value]}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="st-input mb-2 w-full"
        rows={2}
        placeholder="What to follow up about (optional)"
        aria-label="Follow-up note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      {error ? <p className="mb-2 text-xs text-[color:#8d3f2c]">{error}</p> : null}
      <div className="flex gap-1.5">
        <button type="button" className="st-btn" disabled={pending || !on} onClick={() => onSave({ on, priority, note: note.trim() || null })}>
          {visit.followUpOn ? 'Save follow-up' : 'Add follow-up'}
        </button>
        {onCancel ? (
          <button type="button" className="st-btn-quiet" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Whether the customer bought, and what: the orders it is on — an
 * appointment can have several — and the two ways to put it on another:
 * write a new order from it (everything the appointment knows filled in), or
 * link it to an order already written, for a second fitting or the pickup.
 *
 * "Sold" is said here, from the orders (`hasLiveOrder`): the owner's rule
 * since 2026-10-15, which retired the drawer's Sales status select.
 */
/**
 * The orders the appointment is on. A pickup is for an order already
 * written, so it offers only the link — and says so when it has none: one
 * booked with an email no open order has, or several, waits here for a
 * stylist to say which (`docs/23` § "Pickup appointments").
 */
function OrderControl({ visit, pickup }: { readonly visit: VisitRow; readonly pickup: boolean }) {
  const navigate = useNavigate()
  const [linking, setLinking] = useState(false)
  const sold = hasLiveOrder(visit.orders)

  return (
    <div>
      <p className="mb-2 flex items-center gap-2 text-sm" data-testid="sold-state" data-sold={sold}>
        {pickup && !sold ? (
          <span className="st-alert w-full" role="status" data-testid="pickup-unlinked">
            No order linked. Link the order they&rsquo;re picking up, so its pickup date follows this appointment.
          </span>
        ) : pickup ? (
          <span className="st-meta">The order they&rsquo;re picking up.</span>
        ) : sold ? (
          <>
            <span className="st-badge st-badge--submitted">Sold</span>
            <span className="st-meta">They have an order.</span>
          </>
        ) : (
          <span className="st-meta">{visit.orders.length > 1 ? 'No order — the ones on it are canceled.' : visit.orders.length === 1 ? 'No order — the one on it is canceled.' : 'No order yet.'}</span>
        )}
      </p>
      {visit.orders.length > 0 ? (
        <ul className="mb-2 space-y-2">
          {visit.orders.map((ref) => (
            <li key={ref.id}>
              <LinkedOrder orderRef={ref} />
            </li>
          ))}
        </ul>
      ) : null}
      <VisitOrdersTotal orders={visit.orders} />
      <div className={`grid gap-2 ${pickup ? 'grid-cols-1' : 'grid-cols-2'}`}>
        {pickup ? null : (
          <button type="button" className="st-btn-ghost" onClick={() => void navigate(`/admin/orders/new?visit=${encodeURIComponent(visit.id)}`)}>
            <Receipt size={14} aria-hidden />
            Create order
          </button>
        )}
        <button type="button" className="st-btn-ghost" onClick={() => setLinking(true)}>
          <Link2 size={14} aria-hidden />
          Link to order
        </button>
      </div>
      <LinkToOrderDialog visit={visit} open={linking} onClose={() => setLinking(false)} />
    </div>
  )
}

/** One order the appointment is on, as a card that opens it. */
function LinkedOrder({ orderRef }: { readonly orderRef: VisitOrderRef }) {
  const orderId = orderRef.id
  const { token } = useStaffToken()
  const order = useOrder(token, orderId)
  const detail = order.data
  return (
    <Link to={`/admin/orders/${orderId}`} className="st-card-action block px-3 py-2.5 text-sm" data-order-id={orderId}>
      <span className="flex items-center gap-3">
        <Receipt size={16} aria-hidden className="st-meta shrink-0" />
        {detail ? (
          <>
            <span className="min-w-0 flex-1">
              <span className="font-medium tabular-nums">{detail.order.orderNo}</span>
              <span className="st-meta">
                {' '}
                · {detail.lines.length === 0 ? 'nothing on it yet' : `${detail.lines.length} ${detail.lines.length === 1 ? 'line' : 'lines'}`}
                {/* The ref's own total, tax included, so each amount and the
                    Total under the list are read off the same number. A
                    canceled order shows only its Canceled badge. */}
                {orderRef.status === 'canceled' ? null : (
                  <>
                    {' · '}
                    <OrderAmountLabel order={orderRef} />
                  </>
                )}
              </span>
            </span>
            <OrderStatusBadge status={detail.order.status} />
          </>
        ) : (
          <span className="st-meta flex-1">{order.isError ? 'Open the order' : 'Loading the order…'}</span>
        )}
      </span>
    </Link>
  )
}

/** The stylist's notes, edited where they are read. Saved on the button, not on every keystroke. */
function NotesControl({ visit }: { readonly visit: VisitRow }) {
  const { token } = useStaffToken()
  const save = useUpdateAppointment(token, visit.id)
  const [draft, setDraft] = useState(visit.staffNotes ?? '')
  // What the server holds wins whenever it changes underneath — a save from
  // the edit sheet, or a colleague's arriving on refetch.
  useEffect(() => setDraft(visit.staffNotes ?? ''), [visit.staffNotes])
  const dirty = draft.trim() !== (visit.staffNotes ?? '')
  return (
    <div>
      <textarea
        className="st-input w-full"
        rows={3}
        placeholder="Anything the next stylist should know"
        aria-label="Staff notes"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      {dirty ? (
        <div className="mt-1.5 flex gap-1.5">
          <button type="button" className="st-btn" disabled={save.isPending} onClick={() => save.mutate({ staffNotes: draft.trim() || null })}>
            {save.isPending ? 'Saving…' : 'Save notes'}
          </button>
          <button type="button" className="st-btn-quiet" onClick={() => setDraft(visit.staffNotes ?? '')}>
            Discard
          </button>
        </div>
      ) : null}
      {save.isError ? <p className="mt-1 text-xs text-[color:#8d3f2c]">{save.error.message}</p> : null}
    </div>
  )
}

function FittingRoomLinkButton({
  visitId,
  hasSession,
  onMinted,
}: {
  readonly visitId: string
  readonly hasSession: boolean
  readonly onMinted: (url: string) => void
}) {
  const { token } = useStaffToken()
  const mint = useMintAppointmentLink(token, visitId)
  return (
    <button
      type="button"
      className="st-btn-ghost"
      disabled={!hasSession || mint.isPending}
      title={hasSession ? 'Make a new link to their fitting room, to text them' : 'This appointment has no fitting room'}
      onClick={() => mint.mutate(undefined, { onSuccess: (link) => onMinted(link.url) })}
    >
      <Link2 size={14} aria-hidden />
      {mint.isPending ? 'Making…' : 'Fitting room link'}
    </button>
  )
}
